/**
 * royalty-overdue.tsx
 *
 * Outstanding Royalties page — earned (Klopotek) vs SAP balance per author.
 * All calculation and sorting done in SQL via vw_RoyaltyOverdue -> vw_RoyaltyAccountSummary.
 * Click a row to navigate to that author's royalty statements.
 *
 * R10 — Bulk-send statements from Overview: select multiple rows, confirm,
 * enqueue {creditorId, period=lastPeriod} on royalty-email-queue (v1.1.46).
 */

import { Fragment, useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import debounce from "lodash/debounce";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { AlertCircle, ArrowDown, ArrowUp, ArrowUpDown, CheckCircle2, ChevronDown, ChevronRight, Download, Loader2, Mail, RefreshCw, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { PageWrapper } from "@/components/layout/PageWrapper";
import { ViewGuard } from "@/components/auth/PermissionGuards";
import { SlowLoadingNotice } from "@/components/shared/SlowLoadingNotice";
import { useSlowLoadingFlag } from "@/hooks/useSlowLoadingFlag";
import {
  getOverdueRoyalties,
  queueBulkRoyaltyEmails,
  OverdueRoyaltyRow,
  BulkQueueItem,
} from "@/lib/royalty-api";

const formatCurrency = (value: number, currency: string = "EUR"): string => {
  const symbol =
    currency === "USD" ? "$" :
    currency === "GBP" ? "£" :
    currency === "CHF" ? "CHF " : "€";
  return `${symbol}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatPeriod = (dateString: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatSentDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  const d = new Date(dateString);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
};

const rowKey = (row: OverdueRoyaltyRow): string => `${row.accountNumber}-${row.currency}`;

// --- Sorting (all columns sortable — Sanjay feedback, 2026-05-29) ---
type SortKey =
  | "authorName" | "accountNumber" | "currency" | "totalEarned"
  | "sapBalance" | "sapBalanceLocal" | "amountOwed" | "totalTitles" | "lastPeriod" | "lastSentAt";

const SORT_ACCESSORS: Record<SortKey, (r: OverdueRoyaltyRow) => string | number> = {
  authorName: (r) => (r.authorName || "").toLowerCase(),
  accountNumber: (r) => Number(r.accountNumber) || 0,
  currency: (r) => r.currency || "",
  totalEarned: (r) => r.totalEarned ?? 0,
  sapBalance: (r) => r.sapBalance ?? 0,
  sapBalanceLocal: (r) => r.sapBalanceLocal ?? 0,
  amountOwed: (r) => r.amountOwed ?? 0,
  totalTitles: (r) => r.totalTitles ?? 0,
  lastPeriod: (r) => (r.lastPeriod ? new Date(r.lastPeriod).getTime() : 0),
  lastSentAt: (r) => (r.lastSentAt ? new Date(r.lastSentAt).getTime() : 0),
};

// --- Author grouping (Sanjay feedback: combine an author's accounts into one
// hierarchical row). The ONLY key that unifies an author's accounts is the name:
// PROD shows multiple accounts of the same person carry different CREDITOR_ID *and*
// juristic-person ids (e.g. Rochelle Brock 307921 + 307718). We group on the
// normalized first+last name; rows whose name fell back to the account number stay
// solo (keyed by account) so distinct unnamed accounts never merge. ---
type AuthorGroup = {
  key: string;
  authorName: string;
  accounts: OverdueRoyaltyRow[];
  currencies: string[];
  totalEarned: number;
  sapBalance: number;
  sapBalanceLocal: number;
  amountOwed: number;
  amountOwedByCcy: [string, number][];
  totalTitles: number;
  lastPeriod: string | null;
  lastSentAt: string | null;
};

const isFallbackName = (r: OverdueRoyaltyRow): boolean => {
  const n = (r.authorName || "").trim();
  return !n || /^\d+$/.test(n);
};

const groupKeyFor = (r: OverdueRoyaltyRow): string => {
  const fl = `${(r.authorFirstName || "").trim()} ${(r.authorLastName || "").trim()}`.trim().toLowerCase();
  if (isFallbackName(r) || !fl) return `acct:${r.accountNumber}-${r.currency}`;
  return `name:${fl}`;
};

const laterDate = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

const buildAuthorGroups = (data: OverdueRoyaltyRow[]): AuthorGroup[] => {
  const map = new Map<string, OverdueRoyaltyRow[]>();
  for (const r of data) {
    const k = groupKeyFor(r);
    let arr = map.get(k);
    if (!arr) { arr = []; map.set(k, arr); }
    arr.push(r);
  }
  const groups: AuthorGroup[] = [];
  for (const [key, accounts] of map) {
    const byCcy = new Map<string, number>();
    let earned = 0, sap = 0, sapLocal = 0, owed = 0, titles = 0;
    let lp: string | null = null, ls: string | null = null;
    for (const a of accounts) {
      earned += a.totalEarned ?? 0;
      sap += a.sapBalance ?? 0;
      sapLocal += a.sapBalanceLocal ?? 0;
      owed += a.amountOwed ?? 0;
      titles += a.totalTitles ?? 0;
      byCcy.set(a.currency, (byCcy.get(a.currency) ?? 0) + (a.amountOwed ?? 0));
      lp = laterDate(lp, a.lastPeriod);
      ls = laterDate(ls, a.lastSentAt);
    }
    // Children sorted by outstanding, largest first.
    accounts.sort((x, y) => (y.amountOwed ?? 0) - (x.amountOwed ?? 0));
    groups.push({
      key,
      authorName: accounts[0].authorName,
      accounts,
      currencies: [...byCcy.keys()],
      totalEarned: earned,
      sapBalance: sap,
      sapBalanceLocal: sapLocal,
      amountOwed: owed,
      amountOwedByCcy: [...byCcy.entries()],
      totalTitles: titles,
      lastPeriod: lp,
      lastSentAt: ls,
    });
  }
  return groups;
};

const GROUP_SORT: Record<SortKey, (g: AuthorGroup) => string | number> = {
  authorName: (g) => g.authorName.toLowerCase(),
  accountNumber: (g) => Number(g.accounts[0].accountNumber) || 0,
  currency: (g) => g.currencies[0] || "",
  totalEarned: (g) => g.totalEarned,
  sapBalance: (g) => g.sapBalance,
  sapBalanceLocal: (g) => g.sapBalanceLocal,
  amountOwed: (g) => g.amountOwed,
  totalTitles: (g) => g.totalTitles,
  lastPeriod: (g) => (g.lastPeriod ? new Date(g.lastPeriod).getTime() : 0),
  lastSentAt: (g) => (g.lastSentAt ? new Date(g.lastSentAt).getTime() : 0),
};

// --- CSV export ("Download report" — Sanjay feedback). Excel-openable; BOM for UTF-8. ---
const escapeCsv = (v: string | number | null | undefined): string => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const exportRowsToCsv = (data: OverdueRoyaltyRow[]) => {
  const header = ["Author", "Account", "Currency", "Lifetime Royalties", "SAP Balance", "SAP CHF", "Outstanding", "Titles", "Last Period", "Last sent"];
  const lines = data.map((r) =>
    [
      r.authorName,
      r.accountNumber,
      r.currency,
      (r.totalEarned ?? 0).toFixed(2),
      (r.sapBalance ?? 0).toFixed(2),
      (r.sapBalanceLocal ?? 0).toFixed(2),
      (r.amountOwed ?? 0).toFixed(2),
      r.totalTitles,
      r.lastPeriod ? formatPeriod(r.lastPeriod) : "",
      r.lastSentAt ? formatSentDate(r.lastSentAt) : "",
    ].map(escapeCsv).join(",")
  );
  const csv = ["﻿" + header.join(","), ...lines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `outstanding-royalties-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export default function RoyaltyOverduePage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Load the FULL overdue set (not just the top-500-by-outstanding). The heavy
  // view (vw_RoyaltyOverdue -> vw_RoyaltyAccountSummary) materializes every
  // account regardless of LIMIT, so returning all ~15k rows costs ~the same as
  // 500 server-side; search/filter/export then run client-side over the full set.
  const overdueQuery = useQuery({
    queryKey: ["royalty-overdue"],
    queryFn: () => getOverdueRoyalties(100000),
    staleTime: 60_000,
  });
  // isFetching catches initial load AND background refetches; isLoading only catches initial load
  const initialLoad = overdueQuery.isFetching && !overdueQuery.data;
  const isSlow = useSlowLoadingFlag(initialLoad);

  const rows = overdueQuery.data?.rows || [];
  const total = overdueQuery.data?.total || 0;

  // Sort + filter, applied client-side over the loaded rows (Sanjay feedback).
  const [sortKey, setSortKey] = useState<SortKey>("amountOwed");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [minOutstanding, setMinOutstanding] = useState<string>("");

  // Free-text search over author name + account number (debounced — matches the
  // sibling royalty-statements page idiom). Applied client-side over the full set.
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debouncedSetSearch = useCallback(
    debounce((value: string) => setDebouncedSearch(value), 250),
    [],
  );
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSetSearch(value);
  };

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      // text columns default A→Z; numeric/date columns default high→low
      setSortDir(key === "authorName" || key === "currency" ? "asc" : "desc");
    }
  };

  // Which author groups are expanded to show their child accounts.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpand = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Outstanding-amount filter + free-text search, applied per account.
  const filteredRows = useMemo(() => {
    const threshold = parseFloat(minOutstanding);
    const hasThreshold = Number.isFinite(threshold);
    const q = debouncedSearch.trim().toLowerCase();
    if (!hasThreshold && !q) return rows;
    return rows.filter((r) => {
      if (hasThreshold && r.amountOwed < threshold) return false;
      if (q) {
        const name = (r.authorName || "").toLowerCase();
        const acct = String(r.accountNumber ?? "");
        if (!name.includes(q) && !acct.includes(q)) return false;
      }
      return true;
    });
  }, [rows, minOutstanding, debouncedSearch]);

  // Flat sorted view — used for CSV export (one line per account).
  const viewRows = useMemo(() => {
    const acc = SORT_ACCESSORS[sortKey];
    return [...filteredRows].sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortKey, sortDir]);

  // Grouped view — one parent row per author, sorted at group level.
  const groups = useMemo(() => {
    const built = buildAuthorGroups(filteredRows);
    const acc = GROUP_SORT[sortKey];
    return built.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortKey, sortDir]);

  // Render cap — the full filtered set still backs export + counts, but we only
  // paint a bounded number of author groups to keep the DOM responsive. When the
  // user is searching/filtering the match set is small, so render it all; with no
  // filter active we'd otherwise paint ~15k rows, so show the top N by sort order.
  const RENDER_CAP = 200;
  const isFiltering = debouncedSearch.trim().length > 0 || minOutstanding.trim().length > 0;
  const visibleGroups = useMemo(
    () => (isFiltering ? groups : groups.slice(0, RENDER_CAP)),
    [groups, isFiltering],
  );
  const hiddenGroupCount = groups.length - visibleGroups.length;

  // Sortable column header (click to sort / toggle direction).
  const sortHead = (
    k: SortKey,
    label: string,
    align: "left" | "right" | "center" = "left",
    className = "",
  ) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(k)}
        className={`inline-flex items-center gap-1 hover:text-foreground ${
          align === "right" ? "justify-end w-full" : align === "center" ? "justify-center w-full" : ""
        }`}
      >
        {label}
        {sortKey !== k ? (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        ) : sortDir === "asc" ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )}
      </button>
    </TableHead>
  );

  // One account row (either a solo author or a child under an author group).
  const renderAccountRow = (row: OverdueRoyaltyRow, asChild: boolean) => {
    const k = rowKey(row);
    const checked = selected.has(k);
    const eligible = !!row.lastPeriod;
    return (
      <TableRow
        key={k}
        className={`cursor-pointer hover:bg-muted/50 ${asChild ? "bg-muted/20" : ""}`}
        onClick={() => handleRowClick(row)}
      >
        <TableCell onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={checked}
            onCheckedChange={() => toggleRow(row)}
            disabled={!eligible}
            aria-label={`Select ${row.authorName} account ${row.accountNumber}`}
            title={eligible ? "" : "No Last Period — cannot enqueue"}
          />
        </TableCell>
        <TableCell className={asChild ? "pl-10" : "font-medium"}>
          {asChild ? <span className="text-muted-foreground">↳</span> : row.authorName}
        </TableCell>
        <TableCell className="text-right font-mono text-sm">{row.accountNumber}</TableCell>
        <TableCell className="text-center">
          <Badge variant="outline">{row.currency}</Badge>
        </TableCell>
        <TableCell className="text-right text-muted-foreground">
          {formatCurrency(row.totalEarned, row.currency)}
        </TableCell>
        <TableCell className="text-right text-muted-foreground">
          {formatCurrency(row.sapBalance ?? 0, row.currency)}
        </TableCell>
        <TableCell className="text-right text-muted-foreground">
          {formatCurrency(row.sapBalanceLocal ?? 0, "CHF")}
        </TableCell>
        <TableCell className="text-right font-semibold">
          {formatCurrency(row.amountOwed, row.currency)}
        </TableCell>
        <TableCell className="text-center">{row.totalTitles}</TableCell>
        <TableCell className="text-sm">{formatPeriod(row.lastPeriod)}</TableCell>
        <TableCell className="text-sm">
          {row.lastSentAt ? (
            <span
              className="inline-flex items-center gap-1 text-green-700"
              title={`Last sent ${formatSentDate(row.lastSentAt)}${row.lastSentBy ? ` by ${row.lastSentBy}` : ""}${row.sendCount > 1 ? ` · ${row.sendCount} sends total` : ""} (excludes internal addresses)`}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              {formatSentDate(row.lastSentAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>
      </TableRow>
    );
  };

  // An author group: solo accounts render flat; multi-account authors render a
  // parent summary row that expands to its child account rows.
  const renderGroup = (g: AuthorGroup) => {
    if (g.accounts.length === 1) return renderAccountRow(g.accounts[0], false);

    const isOpen = expanded.has(g.key);
    const eligibleKeys = g.accounts.filter((a) => a.lastPeriod).map(rowKey);
    const allSel = eligibleKeys.length > 0 && eligibleKeys.every((k) => selected.has(k));
    const someSel = eligibleKeys.some((k) => selected.has(k)) && !allSel;
    const singleCcy = g.currencies.length === 1 ? g.currencies[0] : null;
    const toggleGroupSel = () =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (allSel) eligibleKeys.forEach((k) => next.delete(k));
        else eligibleKeys.forEach((k) => next.add(k));
        return next;
      });

    return (
      <Fragment key={g.key}>
        <TableRow
          className="cursor-pointer hover:bg-muted/50 font-medium"
          onClick={() => toggleExpand(g.key)}
        >
          <TableCell onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={allSel ? true : someSel ? "indeterminate" : false}
              onCheckedChange={toggleGroupSel}
              disabled={eligibleKeys.length === 0}
              aria-label={`Select all accounts for ${g.authorName}`}
            />
          </TableCell>
          <TableCell>
            <span className="inline-flex items-center gap-1.5">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {g.authorName}
              <Badge variant="secondary">{g.accounts.length} accounts</Badge>
            </span>
          </TableCell>
          <TableCell className="text-right text-muted-foreground text-sm">—</TableCell>
          <TableCell className="text-center">
            <Badge variant="outline">{singleCcy ?? "Mixed"}</Badge>
          </TableCell>
          <TableCell className="text-right text-muted-foreground">
            {singleCcy ? formatCurrency(g.totalEarned, singleCcy) : "—"}
          </TableCell>
          <TableCell className="text-right text-muted-foreground">
            {singleCcy ? formatCurrency(g.sapBalance, singleCcy) : "—"}
          </TableCell>
          <TableCell className="text-right text-muted-foreground">
            {formatCurrency(g.sapBalanceLocal, "CHF")}
          </TableCell>
          <TableCell className="text-right font-semibold">
            {singleCcy ? (
              formatCurrency(g.amountOwed, singleCcy)
            ) : (
              <div className="flex flex-col items-end">
                {g.amountOwedByCcy.map(([c, amt]) => (
                  <span key={c}>{formatCurrency(amt, c)}</span>
                ))}
              </div>
            )}
          </TableCell>
          <TableCell className="text-center">{g.totalTitles}</TableCell>
          <TableCell className="text-sm">{formatPeriod(g.lastPeriod)}</TableCell>
          <TableCell className="text-sm">
            {g.lastSentAt ? (
              formatSentDate(g.lastSentAt)
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </TableCell>
        </TableRow>
        {isOpen && g.accounts.map((a) => renderAccountRow(a, true))}
      </Fragment>
    );
  };

  // Summary stats (note: mixed currencies, so this is approximate)
  const totalOwed = rows.reduce((sum, r) => sum + r.amountOwed, 0);
  const totalEarned = rows.reduce((sum, r) => sum + r.totalEarned, 0);
  const totalSapBalance = rows.reduce((sum, r) => sum + (r.sapBalance ?? 0), 0);

  // R10 — Selection state for bulk send
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);

  const selectedRows = useMemo(() => rows.filter(r => selected.has(rowKey(r))), [rows, selected]);
  const eligibleSelected = useMemo(() => selectedRows.filter(r => r.lastPeriod), [selectedRows]);
  const skippedSelected = selectedRows.length - eligibleSelected.length;

  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of eligibleSelected) {
      map.set(r.currency, (map.get(r.currency) ?? 0) + r.amountOwed);
    }
    return map;
  }, [eligibleSelected]);

  // Select-all targets only the rendered (visible) eligible rows — never the
  // full ~15k set — so an unfiltered "select all" can't accidentally enqueue
  // thousands. Searching/filtering narrows visibleGroups, so it then covers all
  // matches as expected.
  const allSelectableKeys = useMemo(
    () => visibleGroups.flatMap(g => g.accounts).filter(r => r.lastPeriod).map(rowKey),
    [visibleGroups]
  );
  const allEligibleSelected = allSelectableKeys.length > 0 && allSelectableKeys.every(k => selected.has(k));
  const someSelected = selected.size > 0 && !allEligibleSelected;

  const toggleRow = (row: OverdueRoyaltyRow) => {
    const k = rowKey(row);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    if (allEligibleSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(allSelectableKeys));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const bulkSendMutation = useMutation({
    mutationFn: async () => {
      const items: BulkQueueItem[] = eligibleSelected.map(r => ({
        creditorId: r.creditorId,
        period: r.lastPeriod as string,
      }));
      return queueBulkRoyaltyEmails(items);
    },
    onSuccess: (result) => {
      setConfirmOpen(false);
      clearSelection();
      toast({
        title: "Bulk send queued",
        description: `${result.queued} enqueued${result.failed > 0 ? `, ${result.failed} failed at enqueue` : ""}. Tracking id: ${result.enqueueId.slice(0, 8)}…`,
      });
      // Refresh Overview after a delay so "Last sent" column updates as the queue drains
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ["royalty-overdue"] }), 30_000);
    },
    onError: (err: any) => {
      toast({
        title: "Bulk enqueue failed",
        description: err?.message || "Unknown error",
        variant: "destructive",
      });
    },
  });

  const handleRowClick = (row: OverdueRoyaltyRow) => {
    setLocation(`/royalty-statements/${row.creditorId}`);
  };

  const content = (
    <PageWrapper>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Outstanding Royalties</h1>
          <p className="text-muted-foreground">
            Accounts where calculated royalties exceed what SAP shows as settled, sorted by largest amount owed
          </p>
        </div>

        {/* Summary Cards */}
        {!overdueQuery.isLoading && rows.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Outstanding (mixed currencies)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalOwed.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Accounts</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{total.toLocaleString()}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total Earned (mixed currencies)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalEarned.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total SAP Balance (mixed currencies)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{totalSapBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bulk-send action bar (R10) */}
        {rows.length > 0 && selected.size > 0 && (
          <Card className="border-primary bg-primary/5">
            <CardContent className="py-3 flex items-center justify-between gap-4 flex-wrap">
              <div className="text-sm">
                <span className="font-semibold">{selected.size}</span> selected
                {skippedSelected > 0 && (
                  <span className="text-muted-foreground"> ({skippedSelected} have no Last Period and will be skipped)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={clearSelection}>
                  Clear
                </Button>
                <Button
                  size="sm"
                  onClick={() => setConfirmOpen(true)}
                  disabled={eligibleSelected.length === 0}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Send {eligibleSelected.length} statement{eligibleSelected.length === 1 ? "" : "s"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {initialLoad && (
          <div className="space-y-3">
            {isSlow && <SlowLoadingNotice onReload={() => overdueQuery.refetch()} />}
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Loading outstanding royalties…</span>
            </div>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}

        {/* Error State */}
        {overdueQuery.isError && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                <p>Failed to load outstanding royalties: {(overdueQuery.error as Error).message}</p>
              </div>
              <Button variant="outline" size="sm" onClick={() => overdueQuery.refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!overdueQuery.isLoading && !overdueQuery.isError && rows.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">No outstanding royalties found.</p>
            </CardContent>
          </Card>
        )}

        {/* Search + filter + export toolbar */}
        {rows.length > 0 && (
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {/* Free-text search (author name or account number) */}
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  type="search"
                  placeholder="Search author or account…"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-64 h-9 pl-8 pr-8"
                  aria-label="Search by author name or account number"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => { debouncedSetSearch.cancel(); setSearchTerm(""); setDebouncedSearch(""); }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <label htmlFor="min-outstanding" className="text-sm text-muted-foreground whitespace-nowrap">
                Outstanding over
              </label>
              <Input
                id="min-outstanding"
                type="number"
                inputMode="decimal"
                min={0}
                placeholder="amount"
                value={minOutstanding}
                onChange={(e) => setMinOutstanding(e.target.value)}
                className="w-32 h-9"
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap">
                {isFiltering
                  ? `${viewRows.length.toLocaleString()} of ${rows.length.toLocaleString()} match`
                  : `${rows.length.toLocaleString()} accounts`}
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => exportRowsToCsv(selected.size > 0 ? selectedRows : viewRows)}
              disabled={(selected.size > 0 ? selectedRows.length : viewRows.length) === 0}
              title={selected.size > 0
                ? `Export the ${selected.size} selected account(s)`
                : isFiltering
                  ? `Export all ${viewRows.length.toLocaleString()} matching accounts`
                  : `Export all ${rows.length.toLocaleString()} accounts`}
            >
              <Download className="h-4 w-4 mr-2" />
              {selected.size > 0 ? `Download selected (${selected.size})` : "Download report"}
            </Button>
          </div>
        )}

        {/* Table */}
        {rows.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={allEligibleSelected ? true : (someSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleAll}
                        aria-label="Select all eligible rows"
                      />
                    </TableHead>
                    {sortHead("authorName", "Author")}
                    {sortHead("accountNumber", "Account", "right", "text-right")}
                    {sortHead("currency", "Currency", "center", "text-center")}
                    {sortHead("totalEarned", "Lifetime Royalties", "right", "text-right")}
                    {sortHead("sapBalance", "SAP Balance", "right", "text-right")}
                    {sortHead("sapBalanceLocal", "SAP CHF", "right", "text-right")}
                    {sortHead("amountOwed", "Outstanding", "right", "text-right")}
                    {sortHead("totalTitles", "Titles", "center", "text-center")}
                    {sortHead("lastPeriod", "Last Period")}
                    {sortHead("lastSentAt", "Last sent")}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleGroups.length > 0 ? (
                    visibleGroups.map(renderGroup)
                  ) : (
                    <TableRow>
                      <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                        No accounts match{debouncedSearch.trim() ? ` "${debouncedSearch.trim()}"` : " the current filter"}.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {hiddenGroupCount > 0 && (
                <div className="px-4 py-3 text-sm text-muted-foreground border-t">
                  Showing the top {visibleGroups.length.toLocaleString()} of {groups.length.toLocaleString()} authors by{" "}
                  {sortKey === "amountOwed" ? "outstanding amount" : "the current sort"}. Search or filter to find anyone
                  across all {rows.length.toLocaleString()} accounts — the report and search cover the full set.
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* R10 — Confirm bulk send */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send {eligibleSelected.length} royalty statement{eligibleSelected.length === 1 ? "" : "s"}?</DialogTitle>
            <DialogDescription>
              Each selected author will receive an email with the PDF statement for their most recent settlement period. Sends are queued and processed in the background; failures are logged and surface in tomorrow's failed-summary email to royalties@.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <div className="rounded-md border p-3 space-y-1">
              <div className="font-medium">Selection breakdown</div>
              <div className="text-muted-foreground">
                {eligibleSelected.length} statement{eligibleSelected.length === 1 ? "" : "s"} to send
                {skippedSelected > 0 && (
                  <> · {skippedSelected} skipped (no Last Period)</>
                )}
              </div>
              {totalsByCurrency.size > 0 && (
                <div className="text-muted-foreground">
                  Outstanding across selection:{" "}
                  {Array.from(totalsByCurrency.entries())
                    .map(([ccy, amt]) => formatCurrency(amt, ccy))
                    .join(" · ")}
                </div>
              )}
            </div>

            {eligibleSelected.length > 100 && (
              <div className="rounded-md border border-yellow-400 bg-yellow-50 p-3 text-yellow-900">
                <strong>Heads up:</strong> sending more than 100 statements at once. Queue will drain in the background; this is fine, just confirming you meant to send this many.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={bulkSendMutation.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkSendMutation.mutate()}
              disabled={eligibleSelected.length === 0 || bulkSendMutation.isPending}
            >
              {bulkSendMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enqueuing…
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Send {eligibleSelected.length}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageWrapper>
  );

  return <ViewGuard section="royaltyStatements">{content}</ViewGuard>;
}
